import React, { useState, useEffect } from 'react';
import { adminApi } from '../../utils/api';
import { InfoTag } from '../../components/ui/InfoTag';
import { Plus, Edit, Trash2, Eye, EyeOff, Sparkles, Save, X } from 'lucide-react';
import { motion } from 'framer-motion';

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showContentGen, setShowContentGen] = useState(false);

  useEffect(() => {
    loadPosts();
  }, []);

  const getPostIdentifier = (post = {}) => String(post.id || post.slug || '');

  const loadPosts = async () => {
    try {
      const res = await adminApi.getBlogPosts();
      setPosts(res.data);
    } catch (error) {
      console.error('Failed to load blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postIdentifier) => {
    if (window.confirm('Delete this blog post?')) {
      try {
        await adminApi.deleteBlogPost(postIdentifier);
        setPosts((prev) => prev.filter((p) => getPostIdentifier(p) !== postIdentifier));
      } catch (error) {
        alert('Failed to delete post');
      }
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyber-yellow"></div></div>;
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} data-testid="blog-manager">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-2">
          <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Blog Posts</h2>
          <InfoTag label="Blog content" description="Manage editorial blog posts that drive SEO traffic and support AEO citation signals for your content strategy" variant="primary" />
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowContentGen(true)}
            className="btn-secondary inline-flex items-center gap-2 text-sm"
            data-testid="ai-generate-blog-button"
          >
            <Sparkles size={16} />
            AI Generate
          </button>
          <button
            onClick={() => { setEditingPost(null); setShowEditor(true); }}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="new-blog-post-button"
          >
            <Plus size={20} />
            New Post
          </button>
        </div>
      </div>

      {posts.length === 0 ? (
        <div className="terminal-grid p-12 text-center">
          <p className="text-text-muted mb-4">No blog posts yet. Create your first one!</p>
          <button onClick={() => { setEditingPost(null); setShowEditor(true); }} className="btn-primary inline-flex items-center gap-2">
            <Plus size={16} /> Create Post
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => {
            const postIdentifier = getPostIdentifier(post);
            return (
            <div key={postIdentifier || post.title} className="terminal-grid p-5 flex items-center justify-between" data-testid={`blog-item-${post.slug || postIdentifier}`}>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-white font-semibold">{post.title}</h3>
                  {post.published ? (
                    <span className="text-xs px-2 py-0.5 bg-laser-green/20 text-laser-green rounded-full">Published</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full">Draft</span>
                  )}
                  {post.featured && <span className="text-xs px-2 py-0.5 bg-cyber-yellow/20 text-cyber-yellow rounded-full">Featured</span>}
                </div>
                <div className="text-xs text-text-muted font-mono flex items-center gap-4">
                  <span>/{post.slug}</span>
                  {post.category && <span>{post.category}</span>}
                  <span>{post.views || 0} views</span>
                  <span>{post.created_at ? new Date(post.created_at).toLocaleDateString() : 'Date unavailable'}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setEditingPost(post); setShowEditor(true); }} className="btn-secondary text-sm flex items-center gap-1">
                  <Edit size={14} /> Edit
                </button>
                <button onClick={() => handleDelete(postIdentifier)} disabled={!postIdentifier} className="text-red-500 hover:text-red-400 text-sm flex items-center gap-1 disabled:opacity-50">
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      {showEditor && (
        <BlogEditor
          post={editingPost}
          onClose={() => setShowEditor(false)}
          onSave={(savedPost) => {
            const savedIdentifier = getPostIdentifier(savedPost);
            if (editingPost) {
              const editingIdentifier = getPostIdentifier(editingPost);
              setPosts((prev) => prev.map((p) => (getPostIdentifier(p) === editingIdentifier ? savedPost : p)));
            } else {
              setPosts((prev) => {
                if (!savedIdentifier) return [savedPost, ...prev];
                const withoutDuplicate = prev.filter((p) => getPostIdentifier(p) !== savedIdentifier);
                return [savedPost, ...withoutDuplicate];
              });
            }
            setShowEditor(false);
          }}
        />
      )}

      {showContentGen && (
        <ContentGenModal
          onClose={() => setShowContentGen(false)}
          onUseContent={(content) => {
            setEditingPost({ title: '', slug: '', body: content, excerpt: '', tags: [], published: false });
            setShowContentGen(false);
            setShowEditor(true);
          }}
        />
      )}
    </motion.div>
  );
};

// Blog Editor Modal
const BlogEditor = ({ post, onClose, onSave }) => {
  const [formData, setFormData] = useState(post || {
    title: '', slug: '', body: '', excerpt: '', meta_description: '',
    cover_image: '', category: '', tags: [], published: false
  });
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData, tags: formData.tags || [] };
      let response;
      if (post?.id || post?.slug) {
        response = await adminApi.updateBlogPost(post.id || post.slug, payload);
      } else {
        response = await adminApi.createBlogPost(payload);
      }
      onSave(response.data);
    } catch (error) {
      alert('Failed to save post: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const autoSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="blog-editor-modal">
      <div className="terminal-grid p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            {post?.id ? 'Edit Post' : 'New Blog Post'}
          </h3>
          <button onClick={onClose} className="text-text-muted hover:text-white"><X size={24} /></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-text-secondary text-sm mb-2">Title</label>
                <input
                  type="text" value={formData.title} required data-testid="blog-title-input"
                  onChange={(e) => setFormData({ ...formData, title: e.target.value, slug: post?.id ? formData.slug : autoSlug(e.target.value) })}
                  className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow focus:ring-1 focus:ring-cyber-yellow"
                />
              </div>
              <div>
                <label className="block text-text-secondary text-sm mb-2">Slug</label>
                <input
                  type="text" value={formData.slug} required data-testid="blog-slug-input"
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow focus:ring-1 focus:ring-cyber-yellow"
                />
              </div>
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Excerpt</label>
              <input
                type="text" value={formData.excerpt || ''} data-testid="blog-excerpt-input"
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow focus:ring-1 focus:ring-cyber-yellow"
                placeholder="Brief description for previews..."
              />
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Body (Markdown)</label>
              <textarea
                value={formData.body} required data-testid="blog-body-input"
                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow focus:ring-1 focus:ring-cyber-yellow font-mono text-sm"
                rows="12"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-text-secondary text-sm mb-2">Category</label>
                <input
                  type="text" value={formData.category || ''}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow focus:ring-1 focus:ring-cyber-yellow"
                  placeholder="e.g., AI Tools, Tutorials"
                />
              </div>
              <div>
                <label className="block text-text-secondary text-sm mb-2">Cover Image URL</label>
                <input
                  type="url" value={formData.cover_image || ''}
                  onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                  className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow focus:ring-1 focus:ring-cyber-yellow"
                />
              </div>
            </div>

            <div>
              <label className="block text-text-secondary text-sm mb-2">Meta Description</label>
              <input
                type="text" value={formData.meta_description || ''}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow focus:ring-1 focus:ring-cyber-yellow"
                placeholder="SEO meta description..."
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-text-secondary text-sm mb-2">Tags</label>
              <div className="flex gap-2 mb-2 flex-wrap">
                {(formData.tags || []).map(tag => (
                  <span key={tag} className="text-xs px-3 py-1 bg-cyber-yellow/20 text-cyber-yellow rounded-full flex items-center gap-1">
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)}><X size={12} /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text" value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  className="flex-1 px-4 py-2 bg-transparent border border-white/20 text-white focus:border-cyber-yellow text-sm"
                  placeholder="Add tag..."
                />
                <button type="button" onClick={addTag} className="btn-secondary text-sm">Add</button>
              </div>
            </div>

            {/* Published toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox" checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                className="w-5 h-5 accent-cyber-yellow" data-testid="blog-published-toggle"
              />
              <span className="text-text-secondary">Published</span>
              {formData.published ? <Eye size={16} className="text-laser-green" /> : <EyeOff size={16} className="text-text-muted" />}
            </label>
          </div>

          <div className="flex gap-4 mt-8">
            <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="save-blog-post-button">
              <Save size={16} />
              {saving ? 'Saving...' : 'Save Post'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// AI Content Generation Modal
const ContentGenModal = ({ onClose, onUseContent }) => {
  const [topic, setTopic] = useState('');
  const [contentType, setContentType] = useState('blog_post');
  const [tone, setTone] = useState('professional');
  const [length, setLength] = useState('medium');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!topic) return;
    setLoading(true);
    try {
      const res = await adminApi.generateContent({ topic, content_type: contentType, tone, length });
      setContent(res.data.content);
    } catch (error) {
      alert('Generation failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" data-testid="content-gen-modal">
      <div className="terminal-grid p-8 max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            <Sparkles className="text-cyber-yellow" size={24} />
            AI Content Generator
          </h3>
          <button onClick={onClose} className="text-text-muted hover:text-white"><X size={24} /></button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-text-secondary text-sm mb-2">Topic</label>
              <input type="text" value={topic} onChange={(e) => setTopic(e.target.value)}
                className="w-full px-4 py-3 bg-transparent border border-white/20 text-white focus:border-cyber-yellow"
                placeholder="e.g., Best AI Image Generators 2026" data-testid="content-gen-topic" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-text-secondary text-xs mb-1">Type</label>
                <select value={contentType} onChange={(e) => setContentType(e.target.value)}
                  className="w-full px-3 py-2 bg-surface border border-white/20 text-white text-sm">
                  <option value="blog_post">Blog Post</option>
                  <option value="meta_captions">Social Captions</option>
                  <option value="youtube_script">YouTube Script</option>
                  <option value="tool_description">Tool Review</option>
                  <option value="comparison">Comparison</option>
                  <option value="newsletter">Newsletter</option>
                </select>
              </div>
              <div>
                <label className="block text-text-secondary text-xs mb-1">Tone</label>
                <select value={tone} onChange={(e) => setTone(e.target.value)}
                  className="w-full px-3 py-2 bg-surface border border-white/20 text-white text-sm">
                  <option value="professional">Professional</option>
                  <option value="casual">Casual</option>
                  <option value="technical">Technical</option>
                  <option value="persuasive">Persuasive</option>
                </select>
              </div>
              <div>
                <label className="block text-text-secondary text-xs mb-1">Length</label>
                <select value={length} onChange={(e) => setLength(e.target.value)}
                  className="w-full px-3 py-2 bg-surface border border-white/20 text-white text-sm">
                  <option value="short">Short</option>
                  <option value="medium">Medium</option>
                  <option value="long">Long</option>
                </select>
              </div>
            </div>
            <button onClick={handleGenerate} disabled={loading || !topic}
              className="btn-primary w-full" data-testid="generate-content-button">
              {loading ? 'Generating...' : 'Generate Content'}
            </button>
          </div>

          <div className="terminal-grid p-4 bg-surface">
            <h4 className="text-sm font-semibold text-text-secondary mb-3">Preview</h4>
            {content ? (
              <>
                <div className="bg-black/30 p-4 rounded text-text-secondary text-sm whitespace-pre-wrap max-h-60 overflow-y-auto font-mono">
                  {content}
                </div>
                <button onClick={() => onUseContent(content)}
                  className="btn-primary mt-4 w-full text-sm" data-testid="use-generated-content-button">
                  Use This Content in Blog Post
                </button>
              </>
            ) : (
              <div className="text-center text-text-muted py-12">
                <Sparkles size={32} className="mx-auto mb-3 opacity-40" />
                <p className="text-sm">Generated content appears here</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogManager;
